import { ref } from 'vue';
import type { HttpMethod, PathsWithMethod } from 'openapi-typescript-helpers';

import { GLOSS, settings, VORTEX_ACCOUNTS } from '@/constants/settings';
import { getGlossDataQuery, getGlossVersionQuery } from '@/gql/queries';
import type { GlossaryQuery, AccountPageDto } from '@/types/gloss';

import { type FetchOptions } from '@/models/openapi-client';
import { useOpenApiClient } from '@/models/openapi-client';

import type {
  paths,
  HTTPValidationError,
  UploadReplayResponse,
  ErrorMessage,
} from '@/shared/model/types/api-types';
import type { Resources } from '@/composables/data-provider';
import type { Clan, ClanDataResponse } from '@/types/vortex';

export type GlossaryResponse = {
  data: GlossaryQuery;
};

type Paths<M extends HttpMethod> = PathsWithMethod<paths, M>;
type Params<M extends HttpMethod, P extends Paths<M>> = M extends keyof paths[P]
  ? FetchOptions<paths[P][M]>
  : never;

type GlossaryResponseVersion = {
  data: Pick<GlossaryQuery, 'version'>;
};

const GLOSSARY_FULL_URL = settings.glossUrl + GLOSS;
const VORTEX_ACCOUNTS_FULL_URL = settings.glossUrl + VORTEX_ACCOUNTS;

const apiUrlList: Map<keyof Resources, Paths<'get'>> = new Map([
  ['homePage', '/api/public/v1/index/all'],
  ['filter', '/api/public/v1/replays/filter'],
  ['filterAll', '/api/public/v1/filters/all'],
  ['hashtagsAll', '/api/public/v1/hashtags/all'],
  ['actualVideos', '/api/public/v1/index/actual_videos'],
  ['replayById', '/api/public/v1/replays/{replay_id}'],
  ['account', '/api/public/v1/replays/account/{spa_id}'],
  ['contestAll', '/api/public/v1/contests/all'],
  ['contest', '/api/public/v1/contests/{contest_id}'],
]);

export const useNetworkManager = <option, response>(
  r: keyof Resources,
  params?: option,
  options?: {
    immediate?: boolean;
  }
) => {
  const { client: apiClient } = useOpenApiClient();

  const REQ_URL = apiUrlList.get(r)!;

  const state = ref<response>();
  const isReady = ref(false);
  const isFetching = ref(false);

  let error = ref<HTTPValidationError | undefined>(undefined);

  async function execute() {
    isReady.value = false;
    isFetching.value = true;

    const getOptions: Params<'get', typeof REQ_URL> = { params: params || {} };
    const { data, error: fetchError } = await apiClient.GET(REQ_URL, getOptions);

    if (fetchError) {
      error = ref(fetchError);
    } else {
      state.value = data as response;
      isReady.value = true;
    }

    isFetching.value = false;
  }

  if (!options || options?.immediate === true) {
    execute();
  }

  return {
    state,
    isReady,
    isFetching,
    error,
    execute,
  };
};

export const requestAccountComponentsIdByAccountId = async (accountId: number) => {
  const response = await $fetch<AccountPageDto>(VORTEX_ACCOUNTS_FULL_URL + accountId + '/');
  if (response.status !== 'ok' || !response.data[accountId]) return;

  return response.data[accountId]?.dog_tag;
};

export const requestClanInfo = async (accountId: number): Promise<Clan | undefined> => {
  try {
    const url = VORTEX_ACCOUNTS_FULL_URL + `${accountId}/clans/`;
    const { data, status } = await $fetch<ClanDataResponse>(url);

    if (status !== 'ok' || !data.clan) return;

    return data.clan;
  } catch (e) {
    return;
  }
};

export const getGlossaryVersion = async () => {
  const { data } = await $fetch<GlossaryResponseVersion>(GLOSSARY_FULL_URL, {
    method: 'POST',
    body: JSON.stringify({
      variables: {
        lang: 'ru',
      },
      query: getGlossVersionQuery,
    }),
  });

  return data.version;
};

export const getGlossaryData = () => {
  return useFetch<GlossaryResponse>(GLOSSARY_FULL_URL, {
    method: 'POST',
    body: JSON.stringify({
      variables: {
        lang: 'ru',
      },
      query: getGlossDataQuery,
    }),
  });
};

export const useUploadReplay = async (formData: Ref<FormData | null>) => {
  const config = useRuntimeConfig();
  const UPLOAD_URL = `${config.public.apiEndpoint}/api/public/v1/replays/upload`;

  const { data, status, error, execute } = await useAsyncData<UploadReplayResponse, ErrorMessage>(
    'upload-replay',
    async () => {
      return $fetch<UploadReplayResponse>(UPLOAD_URL, {
        method: 'POST',
        body: formData.value,
        credentials: 'include',
      });
    },
    { immediate: false }
  );

  return {
    execute,
    status,
    data,
    error,
  };
};
